import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { currentShift, db, dayTotals, openOrderForTable, tallyShift, topProductsToday } from '../lib/db'
import { clock, elapsedText, money, moneyShort, qtyToText } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { useEffect, useState } from 'react'
import { useNow } from '../lib/hooks'
import { SyncBadge } from '../components/SyncBadge'
import { TableGlyph } from '../components/TableIcon'
import { onSync } from '../lib/sync'

export function HomeScreen() {
  const nav = useNavigate()
  const shop = useApp((s) => s.shop)
  const owner = isOwner(useApp((s) => s.session?.role))

  const now = useNow(60_000)

  // Chủ ở nhà nhìn số của cả quán — phải biết số đó mới tới lúc nào, không thì không
  // phân biệt được "quán chưa bán gì" với "máy chưa kéo được dữ liệu về".
  const [lastPullAt, setLastPullAt] = useState<string | null>(null)
  useEffect(() => onSync((s) => setLastPullAt(s.lastPullAt)), [])
  const totals = useLiveQuery(() => dayTotals(), [], null)
  const shift = useLiveQuery(() => currentShift(), [])
  const shiftTally = useLiveQuery(
    async () => (shift ? await tallyShift(shift) : null),
    [shift, now],
    null,
  )
  const top = useLiveQuery(() => topProductsToday(4), [], [])
  const busy = useLiveQuery(() => db.orders.where('status').equals('Open').count(), [], 0)
  const spendCount = useLiveQuery(
    () => db.expenses.where('spentAt').aboveOrEqual(startOfToday()).count(),
    [],
    0,
  )

  async function quickSell() {
    const takeaway =
      (await db.diningTables.filter((t) => t.isTakeaway).first()) ?? (await db.diningTables.toCollection().first())
    if (!takeaway) return
    const order = await openOrderForTable(takeaway)
    nav(`/order/${order.id}`)
  }

  return (
    <div className="screen">
      <header className="appbar">
        <div className="appbar-title">
          <h1>{shop?.name ?? 'Quán của tôi'}</h1>
          <p>Hôm nay</p>
        </div>
        <SyncBadge />
        <button
          type="button"
          className="icon-btn"
          onClick={() => nav('/settings')}
          aria-label="Cài đặt"
        >
          ⚙
        </button>
      </header>

      <div className="body">
        {/*
          * Người bán hàng không thấy ba con số này. Họ vẫn thấy số tiền phải có trong két
          * ở dải ca bên dưới — đó là thứ họ chịu trách nhiệm — nhưng doanh thu và lãi
          * trong ngày là chuyện của chủ quán.
          */}
        {owner && (
        <div className="tiles">
          <div className="tile in">
            <span className="tile-key">Tiền vô</span>
            <span className="tile-val num">{money(totals?.revenue ?? 0)}</span>
          </div>
          <div className="tile out">
            <span className="tile-key">Tiền ra</span>
            <span className="tile-val num">{money(totals?.expense ?? 0)}</span>
          </div>
          <div className="tile">
            <span className="tile-key">Còn lại</span>
            <span className="tile-val num">{money(totals?.profit ?? 0)}</span>
          </div>
        </div>
        )}

        {/* Ca là thứ chủ quán hỏi đầu tiên khi thuê người bán: két đang phải có bao nhiêu. */}
        <button type="button" className={`shift-strip${shift ? ' is-open' : ''}`} onClick={() => nav('/shift')}>
          {shift ? (
            <>
              <span className="shift-dot" aria-hidden="true" />
              <span className="shift-text">
                <b>Ca mở từ {clock(shift.openedAt)}</b>
                <small>đã {elapsedText(shift.openedAt, now)} · két phải có {money(shiftTally?.expectedCash ?? shift.openingCash)}đ</small>
              </span>
              <span className="shift-go">Chốt ca ›</span>
            </>
          ) : (
            <>
              <span className="shift-text">
                <b>Chưa mở ca</b>
                <small>Mở ca để cuối ngày biết két lệch bao nhiêu</small>
              </span>
              <span className="shift-go">Mở ca ›</span>
            </>
          )}
        </button>

        <div className="big-btns">
          <button type="button" className="big-btn" onClick={() => nav('/tables')}>
            <span className="big-icon ic-brand" aria-hidden="true">
              <TableGlyph />
            </span>
            <span>
              <b>Bán tại bàn</b>
              <small>{busy > 0 ? `${busy} bàn đang có khách` : 'Tất cả bàn trống'}</small>
            </span>
          </button>

          <button type="button" className="big-btn" onClick={quickSell}>
            <span className="big-icon ic-brand" aria-hidden="true">
              ⚡
            </span>
            <span>
              <b>Bán nhanh</b>
              <small>Không cần chọn bàn</small>
            </span>
          </button>

          <button type="button" className="big-btn" onClick={() => nav('/expense')}>
            <span className="big-icon ic-clay" aria-hidden="true">
              🧺
            </span>
            <span>
              <b>Nhập hàng</b>
              <small>{spendCount > 0 ? `${spendCount} phiếu hôm nay` : 'Chưa ghi phiếu nào'}</small>
            </span>
          </button>

          <button type="button" className="big-btn" onClick={() => nav('/history')}>
            <span className="big-icon ic-amber" aria-hidden="true">
              🧾
            </span>
            <span>
              <b>Hoá đơn</b>
              <small>{totals ? `${totals.orderCount} hoá đơn hôm nay` : 'Tra lại đơn đã bán'}</small>
            </span>
          </button>

          {owner && (
          <button type="button" className="big-btn" onClick={() => nav('/report')}>
            <span className="big-icon ic-amber" aria-hidden="true">
              📄
            </span>
            <span>
              <b>Báo cáo thuế</b>
              <small>Sổ doanh thu theo quý</small>
            </span>
          </button>
          )}
        </div>

        {owner && top.length > 0 && (
          <>
            <h2 className="section-label">Bán chạy hôm nay</h2>
            <div className="list">
              {top.map((t) => (
                <div key={t.name} className="list-row">
                  <span className="list-name">{t.name}</span>
                  <span className="list-qty num">{qtyToText(t.qtyMilli)}</span>
                  <span className="list-amount num">{moneyShort(t.revenue)}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {owner && lastPullAt && (
          <p className="foot-note">Số liệu cả quán, cập nhật lúc {clock(lastPullAt)}</p>
        )}

        {owner && totals && totals.orderCount > 0 && (
          <p className="foot-note">
            {totals.orderCount} hoá đơn · tiền mặt {moneyShort(totals.cash)} · chuyển khoản{' '}
            {moneyShort(totals.transfer)}
          </p>
        )}
      </div>
    </div>
  )
}

function startOfToday(): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}
